import { useState } from "react"
import { Link } from "react-router-dom"
import { captureEvent } from "@/lib/posthog"
import { MarketingBrand } from "@/components/landing/MarketingBrand"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Check, AlertCircle, Loader2 } from "lucide-react"
import { BookDemoFormSchema, type BookDemoForm } from "@/lib/schemas"
import { cn } from "@/lib/utils"
import { LandingFooter } from "@/components/landing/LandingFooter"
import { Seo } from "@/components/seo/Seo"

const API_BASE_URL = import.meta.env["VITE_API_BASE_URL"] ?? "/api"

const COVERS = [
  "See a live tender broken into requirements, evaluation criteria, dates, and risks",
  "Watch the AI plan, draft, and revise a response, fully cited",
  "Explore the library and reuse past answers in seconds",
  "See how a bid team collaborates: assignments, reviews, comments",
  "Watch a response exported to a branded Word template",
]

const TickIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" style={{ color: "var(--lp-primary)" }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
)

const ARROW = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
)

export function BookDemoPage() {
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const form = useForm<BookDemoForm>({
    resolver: zodResolver(BookDemoFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      company: "",
      email: "",
      phone: "",
      message: "",
    },
  })

  const {
    register,
    formState: { errors, isSubmitting },
  } = form

  const onSubmit = async (data: BookDemoForm) => {
    setSubmitError(null)
    try {
      const url = new URL(`${API_BASE_URL}/demo-requests`, window.location.origin).toString()
      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null)
        throw new Error(
          (errorBody as { message?: string } | null)?.message ?? "Failed to submit request"
        )
      }

      captureEvent('demo_form_submitted', { source: 'book_a_demo' })
      setSubmitted(true)
    } catch (error) {
      setSubmitError(
        error instanceof Error ? error.message : "Something went wrong. Please try again."
      )
    }
  }

  return (
    <div className="landing-page min-h-screen">
      <Seo
        title="Book a demo - BidContext"
        description="See BidContext analyse a live tender, draft a fully-cited response, and export to your branded Word template. Book a 30-minute walkthrough mapped to how you bid today."
        canonical="/book-a-demo"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.bidcontext.ai/' },
            { '@type': 'ListItem', position: 2, name: 'Book a demo', item: 'https://www.bidcontext.ai/book-a-demo' },
          ],
        }}
      />
      {/* Header — wordmark only */}
      <header className="sticky top-0 z-50" style={{ background: "var(--lp-bg)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-14 flex items-center">
          <Link to="/" className="flex items-center gap-2">
            <MarketingBrand />
          </Link>
        </div>
      </header>

      <main>
        <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
          <div className="grid md:grid-cols-2 gap-12 md:gap-28 lg:gap-40 items-start">
            {/* Left: details */}
            <div>
              <h1 className="lp-display text-[28px] md:text-[36px] leading-[1.15]">
                See it in action
              </h1>
              <p className="text-lg mt-4" style={{ color: "var(--lp-muted)" }}>
                A 30-minute walkthrough, mapped to how you bid today.
              </p>

              <div className="mt-10">
                <div
                  className="text-sm font-medium mb-4"
                  style={{ color: "var(--lp-muted)" }}
                >
                  What we'll cover
                </div>
                <ul className="space-y-3">
                  {COVERS.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-[15px] leading-[1.5]">
                      <span
                        className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 border"
                        style={{ borderColor: "var(--lp-primary)" }}
                      >
                        <TickIcon />
                      </span>
                      <span style={{ color: "var(--lp-fg)" }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

            </div>

            {/* Right: form / success */}
            <div>
              {submitted ? <SuccessState /> : (
                <div
                  className="border lp-hairline rounded-xl p-6 md:p-7"
                  style={{ background: "var(--lp-bg-alt)" }}
                >
                  <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="space-y-4">
                    {submitError && (
                      <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                        <span>{submitError}</span>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field
                        label="First name"
                        id="firstName"
                        type="text"
                        autoComplete="given-name"
                        register={register("firstName")}
                        error={errors.firstName?.message}
                      />
                      <Field
                        label="Last name"
                        id="lastName"
                        type="text"
                        autoComplete="family-name"
                        register={register("lastName")}
                        error={errors.lastName?.message}
                      />
                    </div>

                    <Field
                      label="Company"
                      id="company"
                      type="text"
                      autoComplete="organization"
                      register={register("company")}
                      error={errors.company?.message}
                    />

                    <Field
                      label="Work email"
                      id="email"
                      type="email"
                      autoComplete="email"
                      register={register("email")}
                      error={errors.email?.message}
                    />

                    <Field
                      label="Phone"
                      id="phone"
                      type="tel"
                      autoComplete="tel"
                      register={register("phone")}
                      hint="optional"
                    />

                    <TextareaField
                      label="Message"
                      id="message"
                      placeholder="Tell us about your interest in BidContext"
                      register={register("message")}
                      hint="optional"
                    />

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="lp-btn-primary w-full px-5 py-3 rounded-md text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                      {isSubmitting ? "Sending..." : "Book demo"}
                      {!isSubmitting && ARROW}
                    </button>

                    <p className="text-xs text-center mt-1" style={{ color: "var(--lp-muted)" }}>
                      By submitting, you agree to our{" "}
                      <Link
                        to="/privacy"
                        className="underline underline-offset-2 hover:opacity-70"
                        style={{ color: "var(--lp-fg)" }}
                      >
                        Privacy Policy
                      </Link>
                      .
                    </p>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <LandingFooter />
    </div>
  )
}

// ===================
// Form field
// ===================

type FieldProps = {
  label: string
  id: string
  type: string
  autoComplete?: string | undefined
  hint?: string | undefined
  register: ReturnType<ReturnType<typeof useForm<BookDemoForm>>["register"]>
  error?: string | undefined
}

function Field({ label, id, type, autoComplete, hint, register, error }: FieldProps) {
  return (
    <div>
      <label htmlFor={id} className="flex items-baseline justify-between mb-1.5">
        <span className="text-sm font-medium" style={{ color: "var(--lp-fg)" }}>
          {label}
        </span>
        {hint && (
          <span className="text-xs" style={{ color: "var(--lp-muted)" }}>
            {hint}
          </span>
        )}
      </label>
      <input
        id={id}
        type={type}
        autoComplete={autoComplete}
        {...register}
        className={cn(
          "lp-input w-full px-3 py-2.5 rounded-md border lp-hairline text-sm",
          error && "border-destructive"
        )}
      />
      {error && <p className="text-xs mt-1 text-destructive">{error}</p>}
    </div>
  )
}

type TextareaFieldProps = {
  label: string
  id: string
  placeholder?: string
  hint?: string
  register: ReturnType<ReturnType<typeof useForm<BookDemoForm>>["register"]>
}

function TextareaField({ label, id, placeholder, hint, register }: TextareaFieldProps) {
  return (
    <div>
      <label htmlFor={id} className="flex items-baseline justify-between mb-1.5">
        <span className="text-sm font-medium" style={{ color: "var(--lp-fg)" }}>
          {label}
        </span>
        {hint && (
          <span className="text-xs" style={{ color: "var(--lp-muted)" }}>
            {hint}
          </span>
        )}
      </label>
      <textarea
        id={id}
        rows={3}
        placeholder={placeholder}
        {...register}
        className="lp-input w-full px-3 py-2.5 rounded-md border lp-hairline text-sm resize-none"
      />
    </div>
  )
}

// ===================
// Success
// ===================

function SuccessState() {
  return (
    <div
      className="border lp-hairline rounded-xl p-8 md:p-10 text-center"
      style={{ background: "var(--lp-bg-alt)" }}
    >
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-5"
        style={{ background: "var(--lp-primary-soft)" }}
      >
        <Check className="w-6 h-6" style={{ color: "var(--lp-primary)" }} strokeWidth={2.5} />
      </div>
      <h3 className="lp-display-md text-xl md:text-2xl mb-3" style={{ color: "var(--lp-fg)" }}>
        Thanks. We'll be in touch within 24 hours.
      </h3>
      <Link
        to="/"
        className="text-sm font-medium inline-flex items-center gap-1.5 mt-4"
        style={{ color: "var(--lp-primary)" }}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M12 19l-7-7 7-7" />
        </svg>
        Back to home
      </Link>
    </div>
  )
}
